import React, { useRef, useState } from "react";
import {
  Button,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  ScrollArea,
} from "@semo/shared-ui";
import { ApiError, instance } from "@semo/api-contracts";
import { useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { Upload, FileSpreadsheet, AlertCircle } from "@semo/icons";

export const PRICE_UPLOAD_URL = "/api/v1/items-v2/supplier-prices/bulk-upload";

/** 서버 상한과 같다(`SUPPLIER_PRICE_UPLOAD_MAX_BYTES`). 올리기 전에 먼저 알려 준다. */
const MAX_UPLOAD_BYTES = 10 * 1024 * 1024;

export const PRICE_UPLOAD_MESSAGES = {
  noFile: "파일을 선택해주세요.",
  empty: "빈 파일입니다. 엑셀(.xlsx) 파일을 올려주세요.",
  notXlsx: "엑셀(.xlsx) 파일만 올릴 수 있습니다.",
  tooLarge: "엑셀 파일은 10MB 이하만 올릴 수 있습니다.",
  fallback: "업로드 중 오류가 발생했습니다. 잠시 후 다시 시도해주세요.",
  nothingApplied: "반영된 단가가 없습니다. 아래 오류 내용을 확인해주세요.",
  nothingAppliedAllBlank:
    "반영된 단가가 없습니다. 신규단가 열을 채워 다시 올려주세요.",
} as const;

/**
 * 업로드 실패 사유. 서버가 준 한국어 사유(400)를 그대로 쓰고, 영문·빈 사유면 기본 문구를 쓴다.
 * 예전에는 무슨 일이든 «업로드 중 오류가 발생했습니다» 하나였다(GS 검수 V-12).
 */
const uploadErrorMessage = (error: unknown): string => {
  const apiError = ApiError.fromUnknown(error);
  const message = apiError.userMessage;
  if (apiError.statusCode >= 500 || !/[가-힣]/.test(message)) {
    return PRICE_UPLOAD_MESSAGES.fallback;
  }
  return message;
};

interface UploadError {
  row: number;
  itemId: string | null;
  reason: string;
}

interface UploadResult {
  supplierName: string;
  totalRows: number;
  createdCount: number;
  updatedCount: number;
  skippedCount: number;
  errorCount: number;
  errors: UploadError[];
}

interface SupplierPriceUploadDialogProps {
  isOpen: boolean;
  onOpenChange: (open: boolean) => void;
}

/**
 * 단가 엑셀 일괄 등록.
 *
 * **관리자에게 받은 파일에 단가를 채워 올리는 화면이다.** 품목ID(고유키)는 그 파일에만 있으므로
 * 업체가 스스로 양식을 만들 수는 없다 — 그래서 "양식 받기" 대신 "관리자에게 받은 파일" 이라고
 * 안내한다.
 *
 * 결과는 반드시 화면에 남긴다. 수천 행을 덮어쓰는 조작이라 "완료" 토스트 하나로 끝내면 무엇이
 * 들어갔는지 아무도 모른다.
 */
export const SupplierPriceUploadDialog: React.FC<
  SupplierPriceUploadDialogProps
> = ({ isOpen, onOpenChange }) => {
  const queryClient = useQueryClient();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [file, setFile] = useState<File | null>(null);
  const [isUploading, setIsUploading] = useState(false);
  const [result, setResult] = useState<UploadResult | null>(null);
  /** 마지막 실패 사유 — 토스트는 사라지므로 창 안에도 남긴다. */
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  const close = () => {
    setFile(null);
    setResult(null);
    setErrorMessage(null);
    onOpenChange(false);
  };

  const fail = (message: string) => {
    setErrorMessage(message);
    toast.error(message);
  };

  /** 서버에 보내기 전에 걸러 낼 수 있는 것 — 같은 문구를 서버도 400 으로 돌려준다. */
  const precheck = (selected: File | null): string | null => {
    if (!selected) return PRICE_UPLOAD_MESSAGES.noFile;
    if (selected.size === 0) return PRICE_UPLOAD_MESSAGES.empty;
    if (!selected.name.toLowerCase().endsWith(".xlsx")) {
      return PRICE_UPLOAD_MESSAGES.notXlsx;
    }
    if (selected.size > MAX_UPLOAD_BYTES) return PRICE_UPLOAD_MESSAGES.tooLarge;
    return null;
  };

  const handleUpload = async () => {
    const rejection = precheck(file);
    if (rejection || !file) {
      fail(rejection ?? PRICE_UPLOAD_MESSAGES.noFile);
      return;
    }

    setIsUploading(true);
    setErrorMessage(null);
    try {
      const formData = new FormData();
      formData.append("file", file);

      // ⚠️ Authorization 헤더를 직접 만들지 않는다(GS 검수 V-06). 공용 axios 인스턴스에
      // 지금 세션의 토큰이 이미 심겨 있다 — 임베드 로그인은 토큰을 인스턴스 기본 헤더에만
      // 두고 localStorage 에 쓰지 않으므로, 바깥 로그인 토큰을 꺼내 덮어쓰면 «Bearer null»
      // 이나 만료 토큰이 되어 401 이 났다(useMyCompanyProfile 의 같은 경고 참고).
      // 백엔드는 전 응답을 `{ success, data }` 로 감싼다(ResponseValidationInterceptor).
      const response = await instance.post<{ data: UploadResult }>(
        PRICE_UPLOAD_URL,
        formData,
        { headers: { "Content-Type": "multipart/form-data" } },
      );

      const uploaded = response.data.data;
      setResult(uploaded);

      // 반영된 단가가 목록에 보이도록 다시 받되, **기다리지 않는다** — 느린 «내 상품» 조회까지
      // 끝나야 버튼이 풀리던 탓에 업로드가 끝나고도 4~7초 «반영 중…» 이었다(V-15).
      void queryClient.invalidateQueries();

      const applied = uploaded.createdCount + uploaded.updatedCount;
      if (applied === 0) {
        // 0건 반영은 성공이 아니다 — 머리글이 틀렸거나 전부 오류인 파일이 «성공» 으로 보였다.
        toast.warning(
          uploaded.errorCount > 0
            ? PRICE_UPLOAD_MESSAGES.nothingApplied
            : PRICE_UPLOAD_MESSAGES.nothingAppliedAllBlank,
        );
      } else if (uploaded.errorCount > 0) {
        toast.warning(
          `단가 ${applied.toLocaleString()}건이 반영되었습니다. 반영되지 않은 ${uploaded.errorCount.toLocaleString()}행은 아래에서 확인해주세요.`,
        );
      } else {
        toast.success(`단가 ${applied.toLocaleString()}건이 반영되었습니다.`);
      }
    } catch (error) {
      fail(uploadErrorMessage(error));
    } finally {
      setIsUploading(false);
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={(open) => (open ? null : close())}>
      <DialogContent className="sm:max-w-2xl">
        <DialogHeader>
          <DialogTitle>단가 엑셀 일괄 등록</DialogTitle>
          <DialogDescription>
            관리자에게 받은 단가 엑셀의 <b>신규단가</b> 열을 채워 올려 주세요.
            품목ID 열은 고치면 안 됩니다.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <ul className="text-sm text-slate-600 space-y-1 list-disc pl-5">
            <li>이미 등록된 품목은 올린 단가로 덮어씁니다.</li>
            <li>
              신규단가가 <b>빈 행은 건드리지 않습니다</b> — 취급하지 않을 품목은
              비워 두세요.
            </li>
            <li>
              취급 열에 <b>N</b> 을 적으면 판매중지 상태로 등록됩니다.
            </li>
          </ul>

          <div
            className="border-2 border-dashed rounded-lg p-6 text-center cursor-pointer hover:bg-slate-50"
            onClick={() => fileInputRef.current?.click()}
          >
            <FileSpreadsheet className="w-8 h-8 mx-auto text-slate-400 mb-2" />
            <p className="text-sm text-slate-600">
              {file ? file.name : "클릭해서 엑셀 파일(.xlsx) 선택"}
            </p>
            <input
              ref={fileInputRef}
              type="file"
              // 서버는 .xlsx 만 읽는다(.xls 는 다른 형식이라 400).
              accept=".xlsx"
              aria-label="단가 엑셀 파일 선택"
              className="hidden"
              onChange={(e) => {
                setFile(e.target.files?.[0] ?? null);
                setErrorMessage(null);
                setResult(null);
              }}
            />
          </div>

          {errorMessage && (
            <p
              role="alert"
              className="flex items-start gap-1 rounded-md bg-red-50 px-3 py-2 text-sm text-red-700"
            >
              <AlertCircle className="mt-0.5 h-4 w-4 shrink-0" />
              {errorMessage}
            </p>
          )}

          {result && (
            <div className="rounded-lg border p-4 space-y-2 text-sm">
              <div className="grid grid-cols-4 gap-2 text-center">
                <Stat label="신규" value={result.createdCount} />
                <Stat label="갱신" value={result.updatedCount} />
                <Stat label="건너뜀" value={result.skippedCount} />
                <Stat label="오류" value={result.errorCount} />
              </div>

              {result.errors.length > 0 && (
                <div className="pt-2">
                  <p className="flex items-center gap-1 font-medium text-amber-700 mb-1">
                    <AlertCircle className="w-4 h-4" />
                    반영되지 않은 행
                  </p>
                  <ScrollArea className="h-40 rounded border">
                    <table className="w-full text-xs">
                      <tbody>
                        {result.errors.map((error, index) => (
                          <tr key={index} className="border-b last:border-0">
                            <td className="p-1 w-16 text-slate-500">
                              {error.row > 0 ? `${error.row}행` : "-"}
                            </td>
                            <td className="p-1">{error.reason}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </ScrollArea>
                </div>
              )}
            </div>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={close}>
            닫기
          </Button>
          <Button onClick={() => void handleUpload()} disabled={!file || isUploading}>
            <Upload className="w-4 h-4 mr-1" />
            {isUploading ? "반영 중…" : "업로드"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

const Stat: React.FC<{ label: string; value: number }> = ({ label, value }) => (
  <div className="rounded bg-slate-50 py-2">
    <div className="text-lg font-semibold">{value.toLocaleString()}</div>
    <div className="text-xs text-slate-500">{label}</div>
  </div>
);
